import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, Instagram, Facebook, Youtube, Linkedin, LogIn } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSettings, waLink, telLink } from "../context/SettingsContext";
import { Button } from "./ui";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact Us" },
];

export default function Header() {
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  const logo = settings.logo_url || "/logo.png";
  const mark = settings.logo_icon_url || "/logo-icon.png";
  const showBrand = settings.show_brand_text !== false;
  const brandText = settings.brand_text || "GNONS";
  const socials = [
    { url: settings.instagram, Icon: Instagram },
    { url: settings.facebook, Icon: Facebook },
    { url: settings.youtube, Icon: Youtube },
    { url: settings.linkedin, Icon: Linkedin },
  ].filter((s) => s.url);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 glass border-b transition-all duration-500 ease-premium ${scrolled ? "border-line shadow-soft" : "border-line/60"}`}
      >
        <div className="container-x flex items-center justify-between" style={{ height: scrolled ? 66 : 82 }}>
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0">
            <img src={mark} alt={settings.company_name || "GNONS"} className={`transition-all duration-500 ${scrolled ? "h-9" : "h-11"} w-auto`} />
            {showBrand && (
              <span className="flex flex-col leading-none" data-testid="brand-lockup">
                <span className={`font-extrabold tracking-[0.16em] text-ink transition-all duration-500 ${scrolled ? "text-base" : "text-lg"}`}>{brandText}</span>
                <span className="hidden sm:block text-[0.5rem] tracking-[0.3em] text-gold font-bold mt-1">DESIGN &amp; CONSTRUCTION</span>
              </span>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `link-underline text-[0.92rem] font-semibold transition-colors ${isActive ? "text-gold active" : "text-ink hover:text-gold"}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {settings.crm_url && (
              <a href={settings.crm_url} target="_blank" rel="noreferrer" data-testid="employee-login-desktop" title="Employee Login" className="hidden md:inline-flex items-center gap-1.5 text-[0.8rem] font-semibold text-ink hover:text-gold transition-colors">
                <LogIn size={16} /> Employee Login
              </a>
            )}
            <Button to="/contact" variant="primary" className="hidden md:inline-flex" data-testid="header-consultation-btn">
              Get Free Consultation
            </Button>
            <button
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-line text-ink bg-white/70"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-ink/40 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              data-testid="mobile-menu-panel"
              className="fixed top-0 right-0 bottom-0 z-[70] w-[86%] max-w-sm bg-canvas shadow-lift lg:hidden flex flex-col"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
            >
              <div className="flex items-center justify-between px-5 h-[72px] border-b border-line">
                <Link to="/" className="flex items-center gap-2">
                  <img src={mark} alt="GNONS" className="h-9 w-auto" />
                  {showBrand && <span className="font-extrabold tracking-[0.16em] text-ink text-base">{brandText}</span>}
                </Link>
                <button aria-label="Close menu" data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-line">
                  <X size={20} />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-5 py-4">
                {[...NAV].map((n, i) => (
                  <motion.div key={n.to} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.05 }}>
                    <NavLink
                      to={n.to}
                      end={n.to === "/"}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={({ isActive }) => `block py-3.5 text-lg font-semibold border-b border-line/70 ${isActive ? "text-gold" : "text-ink"}`}
                    >
                      {n.label}
                    </NavLink>
                  </motion.div>
                ))}
                <Button to="/contact" variant="primary" className="w-full mt-5" data-testid="mobile-consultation-btn">Free Consultation</Button>
                {settings.crm_url && (
                  <a href={settings.crm_url} target="_blank" rel="noreferrer" data-testid="employee-login-mobile" className="mt-4 flex items-center justify-center gap-2 py-3 rounded-full border border-line font-semibold text-ink">
                    <LogIn size={16} /> Employee Login
                  </a>
                )}
              </nav>
              <div className="px-5 py-4 border-t border-line bg-sand safe-bottom">
                <div className="grid grid-cols-2 gap-3">
                  <a href={telLink(settings.phone)} data-testid="mobile-call-link" className="flex items-center justify-center gap-2 py-3 rounded-full bg-ink text-white text-sm font-semibold">
                    <Phone size={16} /> Call
                  </a>
                  <a href={waLink(settings.whatsapp, `Hello ${settings.company_name || "GNONS"}, I would like to discuss my project.`)} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-link" className="flex items-center justify-center gap-2 py-3 rounded-full bg-[#25D366] text-white text-sm font-semibold">
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                </div>
                {socials.length > 0 && (
                  <div className="flex items-center gap-3 justify-center mt-4">
                    {socials.map(({ url, Icon }, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-line flex items-center justify-center text-ink hover:text-gold hover:border-gold transition-colors">
                        <Icon size={17} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
